import React, { useState, useEffect } from 'react';
import { tasksAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Target, CheckCircle2, Gift, AlertCircle, History } from 'lucide-react';
import { toast } from 'sonner';

const ClientTasks = ({ kakoId, taskProgress, onProgressUpdate }) => {
  const [claiming, setClaiming] = useState({});
  const [claimHistory, setClaimHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState('current_month');
  const [timeUntilReset, setTimeUntilReset] = useState(0);

  // Cronômetro até fim do mês (reset das tarefas MENSAIS)
  useEffect(() => {
    const calculateTimeUntilEndOfMonth = () => {
      const now = new Date();
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const diff = nextMonth - now;
      return Math.floor(diff / 1000); // Retorna segundos
    };

    setTimeUntilReset(calculateTimeUntilEndOfMonth());

    const timer = setInterval(() => {
      const seconds = calculateTimeUntilEndOfMonth();
      setTimeUntilReset(seconds);
      
      if (seconds <= 0) {
        clearInterval(timer);
        // Recarrega progresso quando resetar
        if (onProgressUpdate) {
          onProgressUpdate();
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (kakoId) {
      loadClaimHistory();
    }
  }, [kakoId, historyFilter]);

  const loadClaimHistory = async () => {
    try {
      // Usa data local do Brasil
      const now = new Date();
      const currentMonth = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
      const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const prevMonthStr = previousMonth.getFullYear() + '-' + String(previousMonth.getMonth() + 1).padStart(2, '0');
      
      const dateFilter = historyFilter === 'current_month' ? currentMonth : 
                        historyFilter === 'previous_month' ? prevMonthStr : null;
      
      const response = await tasksAPI.getMyClaims(kakoId, dateFilter);
      setClaimHistory(response.data);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const handleClaimReward = async (task, progress) => {
    setClaiming({ ...claiming, [task.id]: true });
    
    try {
      const response = await tasksAPI.claimReward(kakoId, task.id);
      toast.success(`🎉 ${response.data.message}`);
      
      // Atualiza o progresso localmente
      if (onProgressUpdate) {
        onProgressUpdate();
      }
      
      // Recarrega histórico
      loadClaimHistory();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao resgatar recompensa';
      toast.error(errorMsg);
    } finally {
      setClaiming({ ...claiming, [task.id]: false });
    }
  };

  if (!taskProgress || taskProgress.length === 0) {
    return null;
  }

  return (
    <Card data-testid="client-tasks-card" className="border-blue-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Target className="text-blue-500" />
            🎯 Tarefas VIP
          </CardTitle>
          {/* Cronômetro de Reset */}
          <div className="text-right">
            <p className="text-xs text-gray-500">Reset em:</p>
            <div className="text-lg font-bold text-orange-600" data-testid="tasks-reset-timer">
              {Math.floor(timeUntilReset / 3600)}h {Math.floor((timeUntilReset % 3600) / 60)}m {timeUntilReset % 60}s
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="active" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            {/* Aba Tarefas Ativas - AZUL */}
            <TabsTrigger 
              value="active"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:bg-blue-100 data-[state=inactive]:text-blue-700"
            >
              <Target className="w-4 h-4 mr-2" />
              Tarefas Ativas
            </TabsTrigger>
            
            {/* Aba Meus Resgates - VERDE */}
            <TabsTrigger 
              value="history"
              className="data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=inactive]:bg-green-100 data-[state=inactive]:text-green-700"
            >
              <History className="w-4 h-4 mr-2" />
              Meus Resgates
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active" className="mt-4">
            <div className="space-y-4">
          {taskProgress.map(({ task, progress }) => {
            const target = task.target_coins || task.target_amount || 0;
            const percentage = Math.min((progress.current_amount / target) * 100, 100);
            const canClaim = progress.completed && !progress.reward_claimed;
            const alreadyClaimed = progress.reward_claimed;

            return (
              <div
                key={task.id}
                data-testid={`client-task-${task.id}`}
                className={`p-4 border-2 rounded-lg ${
                  alreadyClaimed
                    ? 'border-green-200 bg-green-50'
                    : canClaim
                    ? 'border-blue-300 bg-blue-50'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className="space-y-3">
                  {/* Cabeçalho */}
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-lg">{task.title}</h3>
                      <p className="text-sm text-gray-600">{task.description}</p>
                    </div>
                    {alreadyClaimed && (
                      <Badge className="bg-green-600">
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Resgatado
                      </Badge>
                    )}
                    {canClaim && (
                      <Badge className="bg-blue-600 animate-pulse">
                        <Gift className="w-4 h-4 mr-1" />
                        Disponível!
                      </Badge>
                    )}
                  </div>

                  {/* Barra de Progresso */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Progresso</span>
                      <span className="font-bold">
                        {progress.completed ? '100%' : `${Math.floor(percentage)}%`}
                      </span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span>{progress.current_amount.toLocaleString()} / {target.toLocaleString()} 🪙</span>
                      <span className="font-bold text-purple-600">Bônus: {task.reward_coins} moedas</span>
                    </div>
                  </div>

                  {/* Botão de Resgate */}
                  {canClaim && (
                    <Button
                      data-testid={`claim-reward-${task.id}`}
                      onClick={() => handleClaimReward(task, progress)}
                      disabled={claiming[task.id]}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    >
                      {claiming[task.id] ? (
                        'Resgatando...'
                      ) : (
                        <>
                          <Gift className="mr-2 h-4 w-4" />
                          Resgatar {task.reward_coins} Moedas Bônus
                        </>
                      )}
                    </Button>
                  )}

                  {alreadyClaimed && (
                    <Alert className="bg-green-50 border-green-200">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-800">
                        Recompensa resgatada! {task.reward_coins} moedas foram creditadas.
                      </AlertDescription>
                    </Alert>
                  )}

                  {!progress.completed && (
                    <p className="text-xs text-gray-500 text-center">
                      Recarregue mais {(target - progress.current_amount).toLocaleString()} moedas no mês para desbloquear o bônus!
                    </p>
                  )}
                </div>
              </div>
            );
          })}
            </div>

            {/* Alerta com cronômetro */}
            <Alert className={`mt-4 ${timeUntilReset < 86400 ? 'bg-red-50 border-red-200' : ''}`}>
              <AlertCircle className={`h-4 w-4 ${timeUntilReset < 86400 ? 'text-red-600' : ''}`} />
              <AlertDescription className={`text-xs ${timeUntilReset < 86400 ? 'text-red-800' : ''}`}>
                <strong>Atenção:</strong> As tarefas resetam no início do próximo mês. 
                {timeUntilReset < 86400 && (
                  <span className="font-bold"> ⚠️ Menos de 1 dia restante!</span>
                )}
                {' '}Resgate suas recompensas antes do fim do mês!
              </AlertDescription>
            </Alert>
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <div className="space-y-4">
              {/* Filtro de Data */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Filtrar:</span>
                <Select value={historyFilter} onValueChange={setHistoryFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="history-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current_month">Este Mês</SelectItem>
                    <SelectItem value="previous_month">Mês Anterior</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Lista de Resgates */}
              <div className="space-y-3">
                {claimHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <Gift className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">Nenhum resgate encontrado</p>
                  </div>
                ) : (
                  claimHistory.map((claim) => (
                    <div
                      key={claim.claim_id}
                      data-testid={`claim-history-${claim.claim_id}`}
                      className="p-4 border-2 border-green-200 bg-green-50 rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <Gift className="h-4 w-4 text-green-600" />
                            <span className="font-bold">{claim.task_title}</span>
                          </div>
                          <p className="text-sm text-gray-600">{claim.task_description}</p>
                          <p className="text-xs text-gray-500">
                            Data: {claim.date.split('-').reverse().join('/')}
                          </p>
                          <p className="text-xs text-gray-500">
                            Meta atingida: {claim.current_amount.toLocaleString()} 🪙
                          </p>
                        </div>
                        <div className="text-right space-y-1">
                          <Badge className="bg-green-600">
                            <Gift className="w-3 h-3 mr-1" />
                            +{claim.reward_coins} moedas
                          </Badge>
                          {claim.reward_claimed_at && (
                            <p className="text-xs text-gray-500">
                              {new Date(claim.reward_claimed_at).toLocaleTimeString('pt-BR', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ClientTasks;
